/**
 * Building a campaign, and the small readers everything else shares.
 *
 * `createCampaign` is the one place the data tables in `src/data/provinces.ts`
 * and `src/data/nations.ts` are read. After this, the campaign owns a mutable
 * copy of the graph and never looks at the tables again for anything that can
 * change — only for names, flavour and yields, which cannot.
 */

import { Phase, emptyResources } from '../types.ts';
import { createWorld, type World, type WorldConfig } from '../world.ts';
import { generateMap } from '../map.ts';
import { deriveSeed, makeRng } from '../../core/rng.ts';
import {
  LINK_MARCH,
  PROVINCES,
  PROVINCE_TERRAIN,
  linkKindFor,
  provinceDef,
  provinceIndex,
} from '../../data/provinces.ts';
import { NATIONS, nationCapital, nationDef, nationForFaction, nationIndex } from '../../data/nations.ts';
import { confessionFor, confessionHostility } from '../../data/confessions.ts';
import { seedCamps } from './bandits.ts';
import { freshEstates } from './estates.ts';
import { tierGarrison } from './holdings.ts';
import { standingGarrison } from './garrisons.ts';
import { emptyTrade, initTrade, recomputeControl, recomputeLocalValue } from './trade.ts';
import { emptyMarket } from './markettypes.ts';
import { buildSeaLanes } from './fleets.ts';
import { refreshWarnings } from './warnings.ts';
import {
  createDiplomacy,
  OP_BASE,
  OP_CONFESSION_HOSTILITY,
  OP_SAME_CONFESSION,
  SubjectKind,
} from './diplotypes.ts';
import {
  BANDIT_NATION,
  GARRISON_BASE,
  GARRISON_PER_FORT,
  MAX_LOG,
  TurnPhase,
  type CampaignState,
  type Nation,
  type ProvinceLink,
  type ProvinceState,
} from './types.ts';

/** Build the whole strategic board. Deterministic — it draws no random numbers. */
export function createCampaign(playerFactionId: string): CampaignState {
  /**
   * The realm opens at peace, and that is the single largest change this layer
   * has had.
   *
   * Every crown used to start at war with every other crown, which meant war was
   * not a state anything could enter or leave — it was the weather. Nothing could
   * be negotiated because nothing had been decided, and the map read as a brawl
   * because it *was* one. Now the opening position is a cold peace: nobody is
   * shooting, everybody has an opinion, and somebody has to declare.
   *
   * Opinion starts at the confessional term and nothing else. Not at a flat
   * number, because a flat number says the realm has no shape until somebody does
   * something; and not at the full `baseOpinion`, because that reads treaties,
   * wars and borders which either do not exist yet or are about to move. What a
   * crown thinks of its neighbour before anything has happened is exactly what it
   * thinks of their faith.
   */
  const confessions = NATIONS.map((def) => confessionFor(def.factionId, def.id));
  const nations: Nation[] = NATIONS.map((_, i) => ({
    def: i,
    treasury: { ...emptyResources(), gold: 260, iron: 40, grain: 80, timber: 60, stone: 40 },
    /**
     * Thirty men banked, not a hundred and twenty.
     *
     * The old bank was a full host on day one for every crown on the board —
     * seven hundred points out of a capital of a hundred souls, before a single
     * levy had been raised. The owner called it: armies should come out of
     * population, and the machinery already agrees — the monthly levy stream
     * scales with tier, development and unrest (`holdingIncome`), and the
     * estates can strike it to zero. Thirty is seed corn: enough that the first
     * host is a real decision a few months in, arriving faster for a crown with
     * more and better ground, which is exactly the scaling asked for.
     */
    manpower: 30,
    alive: true,
    atWar: NATIONS.map(() => 0),
    relations: NATIONS.map((_, j) => (j === i ? 100 : openingOpinion(confessions[i], confessions[j]))),
    confession: confessions[i],
    liege: -1,
    subject: SubjectKind.None,
    subjectSince: 0,
    // Everybody at home opens indifferent. An estate's standing is earned over a
    // decade of behaviour and nobody has behaved yet.
    estates: freshEstates(),
  }));

  const provinces: ProvinceState[] = PROVINCES.map((def) => {
    const owner = def.owner ? nationIndex(def.owner) : -1;
    const fort = def.fort;
    return {
      owner,
      garrison: owner >= 0 ? GARRISON_BASE + fort * GARRISON_PER_FORT : Math.round(GARRISON_BASE * 0.6),
      fort,
      unrest: 0,
      development: 1,
      // Every holding on the board opens as a hamlet, the player's seat
      // included. The ladder is something a campaign is spent on; nobody is
      // given a head start on it, least of all the twenty-four crowns.
      tier: 1,
      worksTo: 0,
      worksT: 0,
      worksBy: -1,
      siegeBy: -1,
      siegeT: 0,
      claimedBy: -1,
      // Nobody opens with a harbour, including the crowns whose whole country is
      // coast. A port is the first thing a sea power has to decide to be.
      port: 0,
      // Nobody's yards are worth anything until somebody builds some. The
      // player's capital is written every time they walk out to the map.
      barracks: 0,
      // Nowhere is a town of yours until you have stood in it, and nothing on
      // the board opens sacked. The seat is settled below, once the player's
      // crown — and therefore their capital — is known.
      settled: 0,
      wreck: 0,
    };
  });

  const links = buildLinks();

  const playerNation = Math.max(0, nationForFaction(playerFactionId));

  const c: CampaignState = {
    turn: 1,
    phase: TurnPhase.Orders,
    playerNation,
    humanNations: [playerNation],
    capital: Math.max(0, nationCapital(playerNation)),
    nations,
    provinces,
    links,
    armies: [],
    nextArmyId: 1,
    fleets: [],
    nextFleetId: 1,
    lanes: buildSeaLanes(),
    camps: [],
    /**
     * Every crown's seat is a city from the first month, and nothing else is.
     *
     * A warehouse has to exist somewhere or no material a province produces has
     * anywhere to go, and the seat is the one province every nation is
     * guaranteed to hold on turn one. Everything else on the board is chartered
     * the hard way — by building a holding to the sixth rung — which is what
     * makes a second city a thing a crown *achieves*.
     */
    cities: [...new Set(NATIONS.map((_, i) => nationCapital(i)).filter((p) => p >= 0))]
      .sort((a, b) => a - b)
      .map((p) => ({
        at: p,
        owner: provinces[p].owner,
        stock: emptyResources(),
        founded: 1,
      })),
    warnings: [],
    diplo: createDiplomacy(),
    trade: emptyTrade(),
    // The board opens with an empty book: every order on it was posted by
    // somebody, during the campaign, at a price they chose.
    market: emptyMarket(),
    pending: null,
    pendingEvent: null,
    eventHistory: [],
    log: [],
    /**
     * The realm opens in the Ember, exactly as a settlement does.
     *
     * It moves when the player's own capital does — the same sync that carries
     * the city's development and its walls out to the map carries its Age. See
     * `CampaignState.age` for why there is one Age and not seventy-four.
     */
    age: 1,
    winner: -1,
    over: false,
  };

  /**
   * Every seat on the board opens with its own watch already on the walls.
   *
   * `standingGarrison` needs the campaign to exist — it asks which provinces are
   * cities, and the city list is built two fields up — so it cannot be folded
   * into the province literal above. Doing it here rather than leaving it to the
   * first month's regrowth matters: month one is when the AI decides what to
   * march on, and a board whose capitals are briefly soft is a board where the
   * first war of every campaign is chosen against a number that is about to stop
   * being true.
   */
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner < 0) continue;
    c.provinces[p].garrison = Math.max(c.provinces[p].garrison, standingGarrison(c, p));
  }

  // The trade graph is grown from the links and the province table, so it needs
  // the campaign to exist first. It draws no random numbers — `createCampaign`
  // must not, because `loadCampaign` calls it to build its defaults and a loader
  // that consumed the campaign RNG stream would desynchronise every save.
  initTrade(c);

  /**
   * The seat is settled from the first month, because it already is.
   *
   * `main.ts` stands the capital's settlement world up the moment a campaign
   * begins — that world is the other half of the game and nobody has to walk
   * into it — so the province it stands on has to say so, or the door-opening
   * code would offer to found a town on top of the one the player is living in.
   * Every other province on the board opens at zero, which is what it is.
   */
  if (c.provinces[c.capital]) c.provinces[c.capital].settled = 1;

  logLine(c, `Mudmonth, Year 1. ${nationDef(playerNation).name} still has a seat at the table.`);
  return c;
}

/**
 * What one crown thinks of another before anything at all has happened.
 *
 * The `=== 0` is not decoration. `Math.round` of anything in (-0.5, 0) is `-0`,
 * which is a different float from `0`, hashes differently, and is written by
 * `JSON.stringify` as `0` — so it survives a save as the *other* number and the
 * campaign's state hash stops matching itself. The default confessional term
 * lands in exactly that window. See `clampOpinion` in `diplomacy.ts`.
 */
function openingOpinion(a: number, b: number): number {
  const hostility = confessionHostility(a, b);
  const v = Math.round(
    OP_BASE + (hostility === 0 ? OP_SAME_CONFESSION : OP_CONFESSION_HOSTILITY * hostility),
  );
  return v === 0 ? 0 : v;
}

/**
 * The graph, from the one-way declarations in the province table. Every pair is
 * stored once with `a < b`, so hashing and saving are order-independent.
 */
function buildLinks(): ProvinceLink[] {
  const seen = new Set<number>();
  const out: ProvinceLink[] = [];
  for (const def of PROVINCES) {
    const a = provinceIndex(def.id);
    for (const otherId of def.links) {
      const b = provinceIndex(otherId);
      if (b < 0 || b === a) continue;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const key = lo * 1000 + hi;
      if (seen.has(key)) continue;
      seen.add(key);
      const kind = linkKindFor(def.id, otherId);
      const terrainMarch = PROVINCE_TERRAIN[provinceDef(hi).terrain].march;
      out.push({
        a: lo,
        b: hi,
        kind,
        march: Math.max(1, Math.round(LINK_MARCH[kind] * terrainMarch * 0.5 + LINK_MARCH[kind] * 0.5)),
      });
    }
  }
  out.sort((x, y) => x.a - y.a || x.b - y.b);
  return out;
}

/**
 * A world whose only content is the strategic layer.
 *
 * It is a real `World` — same tick, same command door, same hash, same save —
 * because that is what makes the campaign persist properly. It simply has no
 * settlement and no director: `initRun` is never called on it, so there is no
 * Hearth, no villagers and nothing for a director to plan against, and
 * `stepWorld` skips the settlement systems for exactly that reason.
 *
 * It does keep the clock. The Long Ledger runs here as it runs in the city, and
 * `stepRealm` sits a council every time the month turns. The player's actual
 * town is a second `World`, and `stepGame` steps the two of them together.
 */
export function createCampaignWorld(config: WorldConfig): World {
  const w = createWorld(config);
  // Generate the tile map anyway: `loadWorld` always does, so a campaign that
  // did not would fail to round-trip on the very first save.
  generateMap(w.map, makeRng(deriveSeed(config.seed, 31)));
  const c = createCampaign(config.factionId);
  /**
   * Camps are placed here rather than in `createCampaign` because they are
   * rolled, and `createCampaign` draws no random numbers on purpose — it is also
   * what `loadCampaign` builds its defaults from, and a loader that consumed the
   * campaign RNG stream would desynchronise every save it touched.
   */
  seedCamps(c, w.rngCampaign);
  /**
   * …and the markets are told, because a camp chokes what its province yields
   * and `initTrade` settled the opening control before any camp existed.
   *
   * Found by the world generator's rewrite rather than by reasoning: a test
   * asserting that a merchant still on the road is not trade power measured the
   * control of a node, posted the merchant, recomputed, and compared. It passed
   * for as long as the player's first market happened to have no camp in it, and
   * failed the moment a different board put one there — because the recompute
   * was picking up nine months-old camps as well as the merchant. The opening
   * screen was reporting a market share the board had already stopped agreeing
   * with. Neither call draws a random number, so `createCampaign`'s promise to
   * `loadCampaign` is untouched.
   */
  recomputeLocalValue(c);
  recomputeControl(c, 1);
  // And the player starts the campaign knowing what is already on their map.
  refreshWarnings(c);
  w.campaign = c;
  w.phase = Phase.Build;
  w.phaseT = 0;
  return w;
}

// ── Shared reads ───────────────────────────────────────────────────────────

export function logLine(c: CampaignState, text: string): void {
  c.log.push(text);
  if (c.log.length > MAX_LOG) c.log.splice(0, c.log.length - MAX_LOG);
}

/**
 * Garrison cap for a province at its current fortification — and, now, at its
 * tier.
 *
 * A holding that has been built up keeps its own watch. This is where the third
 * rung of the ladder and the tenth are actually felt: the militia adds 45 and
 * the citadel a further 120, on top of whatever the walls are worth. Every
 * caller of this function — regrowth, the AI's threat arithmetic, the world
 * map's panel — gets the tier for free, which is why it lives here rather than
 * being added at the two or three sites that happened to be remembered.
 */
export function garrisonCap(c: CampaignState, p: number): number {
  return (
    GARRISON_BASE +
    c.provinces[p].fort * GARRISON_PER_FORT +
    tierGarrison(c.provinces[p].tier) +
    // The watch the place keeps because of what the place is — nothing on a
    // village, a real number on a city or a seat of a crown. Added here rather
    // than at the four or five sites that happened to be remembered, exactly as
    // the tier was: every caller of the cap is asking how much defence is here,
    // and a capital's own men are defence.
    standingGarrison(c, p)
  );
}

/**
 * The multiplier a defender gets for standing where it stands. Terrain and
 * walls, and nothing else — an attacker can read both off the map before
 * committing, which is the point of a strategic layer.
 */
export function defenceMultiplier(c: CampaignState, p: number): number {
  const terrain = PROVINCE_TERRAIN[provinceDef(p).terrain].defence;
  return terrain * (1 + c.provinces[p].fort * 0.22);
}

/**
 * The ground's own contribution to a defence, without the walls.
 *
 * Split out of `defenceMultiplier` because a bandit camp fights in the hills
 * rather than behind the fortification of the province it squats on — and
 * because a fight on a road belongs to neither province's terrain at all.
 */
export function terrainDefence(p: number): number {
  return PROVINCE_TERRAIN[provinceDef(p).terrain].defence;
}

export function atWar(c: CampaignState, a: number, b: number): boolean {
  if (a < 0 || b < 0 || a === b) return false;
  return (c.nations[a]?.atWar[b] ?? 0) === 1;
}

/** Unclaimed ground is fair game for anyone; owned ground needs a war. */
export function mayAttack(c: CampaignState, nation: number, province: number): boolean {
  const owner = c.provinces[province].owner;
  if (owner < 0) return true;
  if (owner === nation) return false;
  return atWar(c, nation, owner);
}

export function nationName(c: CampaignState, nation: number): string {
  if (nation === BANDIT_NATION) return 'Brigands';
  if (nation < 0) return 'Nobody';
  const n = c.nations[nation];
  return n ? nationDef(n.def).name : 'Nobody';
}

export function provinceName(p: number): string {
  return provinceDef(p).name;
}

/** A nation is finished when it holds nothing and fields nothing. */
export function checkElimination(c: CampaignState): void {
  for (let i = 0; i < c.nations.length; i++) {
    const n = c.nations[i];
    if (!n.alive) continue;
    const holds = c.provinces.some((p) => p.owner === i);
    const fields = c.armies.some((a) => a.owner === i);
    if (!holds && !fields) {
      n.alive = false;
      /**
       * The hulls go with them, and a crown reduced to hulls is still finished.
       *
       * A fleet is deliberately not counted as "fielding something". Ships take
       * no ground and build nothing, so a nation with a navy, no province and no
       * host cannot ever recover — leaving it alive would leave an eliminated
       * power drifting round the map forever with a war it can neither win nor
       * end. Anything still aboard drowns with the rest of them.
       */
      for (let f = c.fleets.length - 1; f >= 0; f--) {
        if (c.fleets[f].owner !== i) continue;
        for (const id of c.fleets[f].cargo) {
          const idx = c.armies.findIndex((a) => a.id === id);
          if (idx >= 0) c.armies.splice(idx, 1);
        }
        c.fleets.splice(f, 1);
      }
      logLine(c, `${nationName(c, i)} is finished. There is nobody left to surrender.`);
    }
  }
  const survivors: number[] = [];
  for (let i = 0; i < c.nations.length; i++) if (c.nations[i].alive) survivors.push(i);
  if (survivors.length === 1 && !c.over) {
    c.over = true;
    c.winner = survivors[0];
    logLine(c, `${nationName(c, survivors[0])} holds the realm. Such as it is.`);
  }
}
